import { captureMessage } from '@sentry/browser';
import type { Client, SSRData } from '@urql/vue';
import { createPinia, type Pinia } from 'pinia';
import type { App, ComponentPublicInstance } from 'vue';

import { prewarmIconComponents } from '@/components/atoms/icon-fi/IconFi.vue';
import {
  initRefreshChannelListener,
  scheduleRefreshFromTokenFunction,
} from '@/composables/tokenRefresh';
import { createApp, setupAppPage } from '@/createApp';
import type { FischerBox } from '@/createGraphQLClient';
import type { DictionaryPhrases } from '@/lib/dictPlugin';
import type { FischerPluginStore } from '@/lib/fischerPlugin';
import smoothScroll, { scrollToHref } from '@/utils/smoothScroll';

import { flushAfterHydration } from './afterHydration';
import {
  createHydrationMismatchReporter,
  type HydrationMismatchHandler,
  installHydrationWarnInterceptor,
} from './hydrationMismatchReporter';
import initSentry from './initSentry';
import processEventQueue from './processEventQueue';
import { installPreloadErrorReload } from './reloadOnPreloadError';
import scrollPageEvent from './scrollPageEvent';
import { installSessionTagProcessor } from './sessionTags';
import { clientDegradedReason, type RehydrateInput, shouldRehydrate } from './shouldRehydrate';
import type { InitialState, SitecoreData, SitecoreSettingsSite } from './sitecoreState';
import {
  flushDiagnosticNotifications,
  queueHydrationMismatchNotification,
  queueSsrErrorNotification,
} from './ssrDiagnosticNotifications';
import type { SsrOperations } from './ssrOperationRecorder';
import type { SsrRestoreReporter } from './ssrRestoreReporter';
import { translatedDomReason } from './translatedDom';
import viewPageEvent from './viewPageEvent';

const importEditorWorkaround = () => import('./editorWorkaround');

declare global {
  var firstState: InitialState | undefined;
  var debugMountedApps: any[];
  var appInitializer: Promise<any>;
  var jssDataResolver: (value: any) => void;
  var jssData: Promise<any>;
  var hydrateAllApps: () => Promise<void>;
}

// eslint falsely applies vue rules

/*
  SSR Data
  If we're running in a server-side rendering scenario,
  the server will provide the window.__JSS_STATE__ object
  for us to acquire the initial state to run with on the client.

  This enables us to skip a network request to load up the layout data.

  SSR is initiated from /server/server.ts.
*/

/**
 * Points the console interceptor below at whichever mini-app is mounting right now, so a mismatch
 * caught through console.warn still carries its JSS rendering name. Mounting is synchronous per app
 * (see MountResult.mount), so a single slot is enough.
 */
let mountingHydrationHandler: HydrationMismatchHandler | null = null;

/**
 * A production build cannot deliver hydration mismatches to app.config.warnHandler at all: Vue reads
 * the component context for a warning from a stack only dev builds push to, so with no instance it
 * bypasses the handler and writes to console.warn instead. Without this the warnHandler path below
 * is dead everywhere except the dev server, which is where the mismatch is least likely to show up.
 *
 * Compiled out where the mismatch messages themselves are compiled out, i.e. UAT and PROD.
 */
if (__VUE_PROD_HYDRATION_MISMATCH_DETAILS__) {
  installHydrationWarnInterceptor(() => mountingHydrationHandler);
}

function getComponentStack(instance: ComponentPublicInstance, maxDepth = 5): string {
  const names: string[] = [];
  let current: ComponentPublicInstance | null = instance;
  let depth = 0;
  while (current && depth < maxDepth) {
    const name = current.$options?.name ?? current.$.type?.name ?? current.$.type?.__name;
    if (name) names.push(name);
    current = current.$parent;
    depth++;
  }
  return names.join(' > ');
}

function getDOMState(rootElement: Element, className: string) {
  try {
    let state = null;
    const jssRawJson = rootElement.getElementsByClassName(className)[0];
    if (jssRawJson) {
      state = JSON.parse(jssRawJson.innerHTML);
    }

    return state;
  } catch (_err) {
    return null;
  }
}

/**
 * The server renders every JSS mini-app separately, each with its own ssrExchange, so a page
 * carries one `__APOLLO_STATE__` block per SSR'd component - while the client runs a single
 * shared urql Client. All blocks therefore have to be merged into one SSRData object before
 * `restoreData`, exactly as the Apollo implementation did via `mergeDeep(...states)`. Reading
 * only the first block silently drops every other component's SSR payload, which makes those
 * components refetch on hydration and flash.
 *
 * Keys are urql operation keys (query + variables hash), so a flat merge is enough: identical
 * keys carry identical results.
 */
const getGraphQLState = (): SSRData | undefined => {
  const elements = Array.from(window.document.querySelectorAll('.__APOLLO_STATE__'));
  if (!elements.length) {
    return undefined;
  }
  const merged = elements.reduce<SSRData>((acc, el) => {
    try {
      return Object.assign(acc, JSON.parse(el.innerHTML));
    } catch {
      return acc;
    }
  }, {});
  return Object.keys(merged).length ? merged : undefined;
};

export interface MountResult {
  app: App<Element>;
  mount: () => ComponentPublicInstance<any>;
  mounted: ComponentPublicInstance<any> | null;
}

const initApp = async (
  root: Element,
  initialState: InitialState | undefined,
  prefetch: any | undefined,
  jsPreload: Promise<any[]> | undefined,
  iconPreload: Promise<void> | undefined,
  rehydrate: boolean | undefined,
  client: Client,
  pinia: Pinia,
  fischerPluginStore: FischerPluginStore,
  dictData: DictionaryPhrases,
  fischerBox: FischerBox,
) => {
  try {
    if (initialState?.ssrErrors) {
      console.error('There were ssr errors', initialState.ssrErrors);
    }

    try {
      await Promise.allSettled([jsPreload, iconPreload].filter(Boolean) as Promise<any>[]);
    } catch (error) {
      console.error('Error during prefetch', error);
    }

    const app = await createApp({
      sitecore: initialState?.sitecore,
      prefetch,
      client,
      pinia,
      rehydrate,
      fischerPluginStore,
      fischerBox,
      dictData,
    });

    if (!app) {
      return null;
    }

    if (REPORT_SSR_DIAGNOSTICS && initialState?.ssrErrors) {
      // Queued, never shown here: this runs before any app has hydrated, and a toast in the store
      // that early breaks the header island. See ssrDiagnosticNotifications.ts.
      queueSsrErrorNotification(app, pinia, initialState);
    }

    app.config.errorHandler = (error: any, instance: any, info: string) => {
      const renderingName = initialState?.sitecore?.rendering?.componentName;
      const vueComponent =
        instance?.$options?.name ?? instance?.$.type?.name ?? instance?.$.type?.__name ?? 'unknown';
      const componentStack = instance ? getComponentStack(instance) : '';
      const msg = `${renderingName}: ${error} ${info} | component: ${vueComponent}${componentStack ? ` | stack: ${componentStack}` : ''}`;
      console.error(msg);
      captureMessage(msg);
    };

    /**
     * Hydration mismatches arrive here like any other warning, but need different handling: they
     * fire once per drifted node, so the generic path below would push the same fault into Sentry
     * dozens of times, and Vue's own message names the innermost component - usually a shared atom -
     * rather than the rendering we set out to mount. The reporter adds that attribution, collapses
     * repeats and caps the volume per page.
     *
     * Console, Sentry, and - on the stages that report SSR diagnostics - a toast, so a mismatch is
     * noticed by whoever is looking at the page rather than only by whoever has the console open.
     * The toast is queued, never shown from here: this runs in the middle of hydration.
     *
     * `report` takes further sinks, but do not add the event hub without the team: it puts a new
     * event type into the pipeline.
     *
     * Reached two ways, and only where __VUE_PROD_HYDRATION_MISMATCH_DETAILS__ is on (showroom, TEST
     * and VM - never UAT or PROD): through app.config.warnHandler below, which names the component
     * but exists only in a dev build, and through the console interceptor at the top of this file,
     * which is what a TEST or VM build actually hits and which substitutes a DOM path for the name.
     */
    const reportHydrationMismatch = createHydrationMismatchReporter({
      jssComponent: initialState?.sitecore?.rendering?.componentName,
      report: (mismatch) => {
        // Checked here and not only before hydrating: a translator can land mid-hydration, and
        // then every text node in this island disagrees for a reason that is not ours.
        if (translatedDomReason(root)) {
          return;
        }
        const where = mismatch.vueComponent === 'unknown' ? '' : ` at <${mismatch.vueComponent}>`;
        const msg = `hydration mismatch in ${mismatch.jssComponent}${where}: ${mismatch.kind}${
          mismatch.location ? `\n  dom: ${mismatch.location}` : ''
        }`;
        // eslint-disable-next-line no-console
        console.warn(msg, mismatch.detail);
        captureMessage(msg);
        if (REPORT_SSR_DIAGNOSTICS) {
          queueHydrationMismatchNotification(app, pinia, mismatch.jssComponent);
        }
      },
    });

    app.config.warnHandler = (error: any, instance: any, info: string) => {
      const renderingName = initialState?.sitecore?.rendering?.componentName;
      const vueComponent =
        instance?.$options?.name ?? instance?.$.type?.name ?? instance?.$.type?.__name ?? 'unknown';
      if (reportHydrationMismatch(String(error), vueComponent, info)) {
        return;
      }
      const msg = `${renderingName}: ${error} ${info} | component: ${vueComponent}`;
      // eslint-disable-next-line no-console
      console.warn(msg);
      captureMessage(msg);
    };

    const fieldKey = initialState?.sitecore?.dataSource?.['conditions-field-key']?.value;
    if (fieldKey) {
      const formElement = document.querySelector(
        `.jss-forms-element[conditions-field-key="${fieldKey}"]`,
      );
      // Class 'jss-init-disabled' is added on start dragging elements (see 'FormDesignBoard.js' in fischer-website sitecore project)
      if (formElement?.classList?.contains('jss-init-disabled')) {
        return null;
      }
    }

    const result: MountResult = {
      app: app,
      mount: () => {
        mountingHydrationHandler = reportHydrationMismatch;
        try {
          result.mounted = app.mount(root);
        } finally {
          mountingHydrationHandler = null;
        }
        return result.mounted;
      },
      mounted: null,
    };

    return result;
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * The debug companion to getGraphQLState, merged the same way and for the same reason: one block
 * per SSR'd component. Says which operation and which variables produced each payload key, which
 * is what lets the client name the variable behind a restore miss. Debug builds only - the block
 * is not emitted anywhere else.
 */
const getSsrOperations = (): SsrOperations | undefined => {
  if (!DEBUG_SSR_OPERATIONS) {
    return undefined;
  }
  const elements = Array.from(window.document.querySelectorAll('.__SSR_OPERATIONS__'));
  const merged = elements.reduce<SsrOperations>((acc, el) => {
    try {
      return Object.assign(acc, JSON.parse(el.innerHTML));
    } catch {
      return acc;
    }
  }, {});
  return Object.keys(merged).length ? merged : undefined;
};

const initAllApps = async () => {
  const ssrState = getGraphQLState();
  const recordedSsrOperations = getSsrOperations();
  let fischerPluginStore: FischerPluginStore;
  let client: Client;
  let dictData: DictionaryPhrases;
  let fischerBox: FischerBox;
  let ssrRestore: SsrRestoreReporter | undefined;
  const pinia = createPinia();
  let layoutSucceeded: boolean = false;
  const jssDataJson = document.querySelector('script.jss-data')?.innerHTML;
  const jssSettingsJson = document.querySelector('script.jss-settings')?.innerHTML;
  let sitecoreData: SitecoreData | undefined;
  // script.jss-settings is the *site* object, not the settings around it - hence the wrapping at
  // both the places that build an InitialState from it.
  let sitecoreSettings: SitecoreSettingsSite | undefined;
  if (!IS_SHOWROOM) {
    try {
      if (!jssDataJson) {
        throw new Error('missing data');
      }
      if (!jssSettingsJson) {
        throw new Error('missing settings');
      }
      sitecoreData = JSON.parse(jssDataJson);
      sitecoreSettings = JSON.parse(jssSettingsJson);
    } catch (error) {
      console.error('error parsing jss-data', error);
    }
  }

  window.hydrateAllApps = async () => {
    const jssComponents = document.querySelectorAll('.jss-component:not(.inited)');
    if (jssComponents.length > 0) {
      const apps: [
        Element,
        InitialState | undefined,
        any,
        Promise<any[]> | undefined,
        Promise<void> | undefined,
        RehydrateInput,
      ][] = [];
      const editorPreloads = [];
      for (const jssComponent of Array.from(jssComponents)) {
        jssComponent.classList.add('inited');
        const root = jssComponent.getElementsByClassName('root')[0];
        const stateScript = jssComponent.getElementsByClassName('__JSS_STATE__')[0] as
          | HTMLElement
          | undefined;
        const ssrAttribute = stateScript?.dataset.ssr;
        const degraded = stateScript?.dataset.degraded;

        const initialState =
          (getDOMState(jssComponent, '__JSS_STATE__') as InitialState) || undefined;
        if (initialState) {
          if (!initialState.sitecore) {
            // @ts-expect-error invalid state
            initialState.sitecore = {};
          }
          // @ts-expect-error fallback not detected
          if (!initialState.sitecore.data) {
            // @ts-expect-error fallback not detected
            initialState.sitecore.data = sitecoreData;
          }
          // @ts-expect-error fallback not detected
          if (!initialState.sitecore.settings) {
            // @ts-expect-error fallback not detected
            initialState.sitecore.settings = { site: sitecoreSettings };
          }
        }
        // Decided below, once setupAppPage has settled whether the client has a dictionary.
        const rehydrateInput: RehydrateInput = {
          ssrAttribute,
          degraded,
          isEditMode: sitecoreData?.isEditMode,
          isShowroom: IS_SHOWROOM,
          hasSsrErrors: Boolean(initialState?.ssrErrors),
          appEnvironment: APP_ENVIRONMENT,
        };
        const prefetch = getDOMState(jssComponent, '__PREFETCH__');
        const jsPreloadFiles: [string] = getDOMState(jssComponent, '__JS_PRELOAD__');
        const jsPreload = jsPreloadFiles
          ? Promise.all(jsPreloadFiles.map((file: string) => import(/* @vite-ignore */ file)))
          : undefined;
        editorPreloads.push(jsPreload);

        const iconModulePaths: string[] | null = getDOMState(jssComponent, '__ICON_PRELOAD__');
        const iconPreload = iconModulePaths ? prewarmIconComponents(iconModulePaths) : undefined;

        if (initialState || IS_SHOWROOM) {
          apps.push([root, initialState, prefetch, jsPreload, iconPreload, rehydrateInput]);
        }
      }

      // setup the page to the first app, since all apps should result in the same page

      if (!window.firstState) {
        window.firstState =
          apps[0]?.[1] ||
          (sitecoreData && sitecoreSettings
            ? ({
                sitecore: {
                  data: sitecoreData,
                  settings: sitecoreSettings,
                },
              } as InitialState)
            : undefined);

        ({ client, layoutSucceeded, fischerPluginStore, fischerBox, dictData, ssrRestore } =
          await setupAppPage({
            initialState: window.firstState,
            ssrState,
            ssrOperations: recordedSsrOperations,
            pinia,
          }));
      }

      if (layoutSucceeded) {
        // One verdict for every island: the dictionary is loaded once for the whole page.
        const clientDegraded = clientDegradedReason(sitecoreSettings?.dictionary, dictData);
        const appContextsWithNull = await Promise.all(
          apps.map(([root, initialState, prefetch, jsPreload, iconPreload, rehydrateInput]) =>
            initApp(
              root,
              initialState,
              prefetch,
              jsPreload,
              iconPreload,
              shouldRehydrate({
                ...rehydrateInput,
                degraded: rehydrateInput.degraded ?? clientDegraded,
                translated: translatedDomReason(root),
              }) || undefined,
              client,
              pinia,
              fischerPluginStore,
              dictData,
              fischerBox,
            ),
          ),
        );
        // this can be removed with typescript 5.5

        function notEmpty<TValue>(value: TValue | null): value is TValue {
          return value !== null;
        }
        const appContexts: MountResult[] = appContextsWithNull.filter(notEmpty);

        if (['TEST', 'UAT', 'PROD'].includes(APP_ENVIRONMENT) && FRONTEND_RELEASE) {
          if (appContexts.length) {
            const appsArray = appContexts.map((context) => context.app);
            // @ts-ignore
            appsArray.config = {}; // bug in Sentry?
            initSentry(appsArray);
          }
        }
        // Before mounting, so the hydration mismatches raised during it are tagged too.
        installSessionTagProcessor(fischerBox);

        const isEditMode = window.firstState?.sitecore?.data?.isEditMode;
        if (isEditMode) {
          try {
            // give apps the best chance to mount at tbe same time
            await Promise.all(editorPreloads);
          } catch (error) {
            console.error('Error during prefetch', error);
          }
        }
        // Synchronous per island, so one island's onMounted runs before the next island's setup: a
        // shared-store write from a mounted hook drifts the *next* island's hydration render. Queue
        // it with runAfterHydration instead.
        const mountedApps = appContexts.map((context) => context.mount());
        window.debugMountedApps = await Promise.all(mountedApps);

        // Hydration is over, so touching a shared store is safe again.
        flushAfterHydration();
        // And every operation from here on is ordinary interaction, which keys differently on
        // purpose - so the restore comparison closes its window at the same point.
        ssrRestore?.finish();
        if (REPORT_SSR_DIAGNOSTICS) {
          flushDiagnosticNotifications();
        }
        if (isEditMode) {
          const editorWorkaround = await importEditorWorkaround();
          editorWorkaround.default();
        } else {
          const firstApp = appContexts[0].app as any;
          if (!firstApp) {
            console.error('eventhub view-page has no app');
            return;
          }
          const $fischer = firstApp.$fischer || firstApp.config?.globalProperties?.$fischer;
          const $cookies = firstApp.$cookies || firstApp.config?.globalProperties?.$cookies;
          if (!$fischer || !$cookies) {
            console.error('missing properties for events');
            return;
          }

          initRefreshChannelListener($fischer, $cookies);
          scheduleRefreshFromTokenFunction($fischer, $cookies);
          viewPageEvent($fischer, $cookies);
          scrollPageEvent($fischer, $cookies);
          processEventQueue($fischer, $cookies);
        }
      } else {
        console.info('Did not receive layout, not initializing apps');
      }
    }
  };
  await window.hydrateAllApps();

  return async (element: Element, state: InitialState) => {
    const formsAppPage = setupAppPage({
      initialState: state,
      ssrState,
      noLayoutData: true,
      pinia,
    });
    const { client: formsClient, fischerBox: formsBox } = await formsAppPage;

    const context = await initApp(
      element,
      state,
      undefined,
      undefined,
      undefined,
      false,
      formsClient,
      pinia,
      fischerPluginStore,
      dictData,
      formsBox,
    );
    if (!context) {
      console.error('did not create app');
      return null;
    }
    installSessionTagProcessor(formsBox);
    context.mount();

    // This one client renders (rehydrate: false), so there is no hydration to disturb - but it
    // still has to be drained, or a forms app's ssr error would queue a toast nobody ever shows.
    flushAfterHydration();
    if (REPORT_SSR_DIAGNOSTICS) {
      flushDiagnosticNotifications();
    }
    return context;
  };
};

if (!window.appInitializer) {
  // before initAllApps, which starts the first lazy chunk loads
  installPreloadErrorReload();
  window.jssData = new Promise((resolve) => {
    window.jssDataResolver = resolve;
  });
  window.appInitializer = initAllApps();
  window.appInitializer.then(() => {
    // jump to anchor in url, delay to undo the browser's automatic scrolling
    scrollToHref(String(window.location));
    smoothScroll(document);
  });
} else {
  console.debug('appInitializer already present');
}
